import { PlacedPlant, BedSpec, PlanItem } from '@/lib/companionPlanting';
import { getPlantEmoji, getPlantImage } from '@/lib/plantEmojis';

interface Props {
  bed: BedSpec;
  placements: PlacedPlant[];
  plan: PlanItem[];
}

const LAYER_FILL: Record<string, string> = {
  tall: 'hsl(var(--garden-moss) / 0.14)',
  mid: 'hsl(var(--garden-leaf) / 0.14)',
  ground: 'hsl(var(--garden-sage) / 0.14)',
  edge: 'hsl(var(--garden-bloom) / 0.14)',
};

const LAYER_STROKE: Record<string, string> = {
  tall: 'hsl(var(--garden-moss))',
  mid: 'hsl(var(--garden-leaf))',
  ground: 'hsl(var(--garden-sage))',
  edge: 'hsl(var(--garden-bloom))',
};

export function BedDiagram({ bed, placements, plan }: Props) {
  const padding = 30;
  const w = bed.length_cm + padding * 2;
  const h = bed.width_cm + padding * 2;

  // Group placements by legendIndex (one cluster per crop) for halos
  const clusters = new Map<number, PlacedPlant[]>();
  for (const p of placements) {
    if (!clusters.has(p.legendIndex)) clusters.set(p.legendIndex, []);
    clusters.get(p.legendIndex)!.push(p);
  }

  // Map plan by legendIndex for emoji lookup
  const planByLegend = new Map<number, PlanItem>();
  for (const it of plan) planByLegend.set(it.legendIndex, it);

  // Pre-resolve image URLs (so SVG can <image> them)
  const imageByLegend = new Map<number, string | null>();
  const emojiByLegend = new Map<number, string>();
  for (const it of plan) {
    imageByLegend.set(it.legendIndex, getPlantImage(it.name));
    emojiByLegend.set(it.legendIndex, getPlantEmoji(it.name));
  }

  return (
    <div className="grid gap-4 md:grid-cols-[1fr,200px]">
      {/* Diagram */}
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-soft">
        <svg
          viewBox={`0 0 ${w} ${h}`}
          className="w-full h-auto"
          role="img"
          aria-label="Top-down companion planting diagram"
        >
          {/* Wood-frame outer */}
          <rect
            x={padding - 10} y={padding - 10}
            width={bed.length_cm + 20} height={bed.width_cm + 20}
            rx={14}
            fill="hsl(28 35% 42%)"
          />
          {/* Soil */}
          <rect
            x={padding} y={padding}
            width={bed.length_cm} height={bed.width_cm}
            rx={10}
            fill="hsl(28 30% 22%)"
            stroke="hsl(28 40% 15%)"
            strokeWidth={2}
          />

          <text
            x={padding + bed.length_cm / 2}
            y={padding - 18}
            textAnchor="middle"
            fontSize={16}
            fill="hsl(var(--muted-foreground))"
          >
            back
          </text>
          <text
            x={padding + bed.length_cm / 2}
            y={padding + bed.width_cm + 22}
            textAnchor="middle"
            fontSize={16}
            fill="hsl(var(--muted-foreground))"
          >
            {bed.sun_exposure === 'full_sun' ? '☀ full sun side' : bed.sun_exposure === 'partial_shade' ? '⛅ partial shade' : '🌥 shade'}
          </text>

          {/* Plant glyphs — emoji or PNG image at each placement */}
          {placements.map((p, i) => {
            const cx = padding + p.x;
            const cy = padding + p.y;
            // Glyph size: scale with footprint but clamp so it stays readable
            const size = Math.max(28, Math.min(p.radius * 1.7, 70));
            const img = imageByLegend.get(p.legendIndex);
            if (img) {
              return (
                <image
                  key={`p-${i}`}
                  href={img}
                  x={cx - size / 2}
                  y={cy - size / 2}
                  width={size}
                  height={size}
                />
              );
            }
            return (
              <text
                key={`p-${i}`}
                x={cx}
                y={cy + size * 0.35}
                textAnchor="middle"
                fontSize={size}
                style={{ userSelect: 'none' }}
              >
                {emojiByLegend.get(p.legendIndex) ?? '🌱'}
              </text>
            );
          })}
        </svg>
      </div>

      {/* Legend */}
      <div className="rounded-2xl border border-border/60 bg-card p-3 shadow-soft">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Legend</p>
        <ul className="space-y-2">
          {plan.map(item => {
            const img = getPlantImage(item.name);
            return (
              <li key={item.legendIndex} className="flex items-center gap-2 text-sm">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background border border-border/60">
                  {img ? (
                    <img src={img} alt="" width={20} height={20} className="object-contain" />
                  ) : (
                    <span style={{ fontSize: 18, lineHeight: 1 }}>{getPlantEmoji(item.name)}</span>
                  )}
                </span>
                <span className="flex-1 truncate text-foreground">{item.name}</span>
                <span className="text-xs font-semibold text-muted-foreground">×{item.quantity}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
